import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import {
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle,
  Clock,
  X,
  Bell,
  Settings,
} from 'lucide-react';

const SystemAlertsPanel = () => {
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [darkMode, setDarkMode] = useState(
    () => window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  useEffect(() => {
    setIsLoading(true);
    const q = query(collection(firestore, 'alerts'), orderBy('timestamp', 'desc'));
    const unsub = onSnapshot(
      q,
      snapshot => {
        setAlerts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setIsLoading(false);
      },
      err => {
        setIsLoading(false);
      }
    );
    const darkListener = e => setDarkMode(e.matches);
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', darkListener);
    return () => {
      unsub();
      window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', darkListener);
    };
  }, []);

  const alertConfig = {
    critical: {
      icon: AlertTriangle,
      bg: 'bg-error/10',
      text: 'text-error',
      border: 'border-error/20',
      priority: 'High Priority',
    },
    warning: {
      icon: AlertCircle,
      bg: 'bg-warning/10',
      text: 'text-warning',
      border: 'border-warning/20',
      priority: 'Medium Priority',
    },
    info: {
      icon: Info,
      bg: 'bg-accent/10',
      text: 'text-accent',
      border: 'border-accent/20',
      priority: 'Low Priority',
    },
    success: {
      icon: CheckCircle,
      bg: 'bg-success/10',
      text: 'text-success',
      border: 'border-success/20',
      priority: 'Low Priority',
    },
  };

  const filters = [
    { value: 'all', label: 'All Alerts' },
    { value: 'critical', label: 'Critical' },
    { value: 'warning', label: 'Warnings' },
    { value: 'unread', label: 'Unread' },
    { value: 'action', label: 'Action Required' },
  ];

  const filteredAlerts = alerts.filter(alert => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !alert.isRead;
    if (filter === 'action') return alert.actionRequired;
    return alert.type === filter;
  });

  const markAsRead = alertId => {
    setAlerts(prev =>
      prev.map(alert => (alert.id === alertId ? { ...alert, isRead: true } : alert))
    );
  };

  const dismissAlert = alertId => {
    setAlerts(prev => prev.filter(alert => alert.id !== alertId));
  };

  const getTimeAgo = timestamp => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const unreadCount = alerts.filter(alert => !alert.isRead).length;
  const criticalCount = alerts.filter(alert => alert.type === 'critical').length;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const alertVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 20 },
  };

  if (isLoading) {
    return (
      <div className="rounded-xl border border-accent/20 bg-bg-card p-4 shadow-sm dark:border-accent/40 dark:bg-nav md:p-6">
        <div className="animate-pulse">
          <div className="mb-6 flex items-center justify-between">
            <div className="h-6 w-32 rounded bg-accent/10 dark:bg-accent/90"></div>
            <div className="h-8 w-24 rounded bg-accent/10 dark:bg-accent/90"></div>
          </div>
          <div className="space-y-4">
            {[...Array(4)].map((_, index) => (
              <div
                key={index}
                className="rounded-lg border border-accent/20 p-4 dark:border-accent/40"
              >
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-full bg-accent/10 dark:bg-accent/90"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-48 rounded bg-accent/10 dark:bg-accent/90"></div>
                    <div className="h-3 w-64 rounded bg-accent/10 dark:bg-accent/90"></div>
                    <div className="h-3 w-24 rounded bg-accent/10 dark:bg-accent/90"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="rounded-xl border border-accent/20 bg-bg-card p-4 shadow-sm transition-colors duration-300 dark:border-accent/40 dark:bg-nav md:p-6"
    >
      <div className="mb-4 flex flex-col gap-3 md:mb-6 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <div className="relative rounded-lg bg-accent p-2 dark:bg-accent/80">
            <Bell className="h-5 w-5 text-bg-card dark:text-bg-main" />
            {unreadCount > 0 && (
              <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-bg-card dark:bg-primary/80">
                {unreadCount}
              </span>
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              System Alerts
            </h3>
            <p className="text-sm text-gray-900/60">
              {criticalCount} critical alerts require attention
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="rounded-lg p-2 text-accent/40 transition-colors hover:bg-accent/10 hover:text-accent dark:hover:bg-accent/20"
          >
            <Settings className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="mb-4 flex items-center gap-2 overflow-x-auto md:mb-6">
        {filters.map(filterOption => (
          <button
            key={filterOption.value}
            onClick={() => setFilter(filterOption.value)}
            className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
              filter === filterOption.value
                ? 'bg-accent/10 text-accent'
                : 'text-accent hover:bg-accent/5 hover:text-text-primary dark:hover:bg-accent/20 dark:hover:text-bg-main'
            }`}
          >
            {filterOption.label}
          </button>
        ))}
      </div>
      <div className="max-h-[60vh] space-y-3 overflow-y-auto md:max-h-96 md:space-y-4">
        <AnimatePresence>
          {filteredAlerts.map(alert => {
            const config = alertConfig[alert.type];
            const AlertIcon = config.icon;
            return (
              <motion.div
                key={alert.id}
                variants={alertVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className={`rounded-lg border p-4 ${config.border} ${config.bg} ${!alert.isRead ? 'ring-2 ring-accent/20 dark:ring-accent/40' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`rounded-lg p-2 ${config.bg}`}>
                    <AlertIcon className={`h-5 w-5 ${config.text}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="mb-1 flex items-center gap-2">
                          <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                            {alert.title}
                          </h4>
                          {alert.actionRequired && (
                            <span className="rounded-full bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent">
                              Action Required
                            </span>
                          )}
                        </div>
                        <p className="mb-2 text-sm text-gray-900/80 dark:text-gray-200">
                          {alert.message}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-gray-900/60">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {getTimeAgo(alert.timestamp)}
                          </span>
                          <span className="capitalize">{alert.category}</span>
                          <span>{config.priority}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {!alert.isRead && (
                          <button
                            onClick={() => markAsRead(alert.id)}
                            className="rounded p-1 text-accent/40 transition-colors hover:bg-accent/10 hover:text-accent dark:hover:bg-accent/20"
                            title="Mark as read"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => dismissAlert(alert.id)}
                          className="rounded p-1 text-accent/40 transition-colors hover:bg-accent/10 hover:text-accent dark:hover:bg-accent/20"
                          title="Dismiss alert"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
        {filteredAlerts.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="py-8 text-center"
          >
            <CheckCircle className="mx-auto mb-4 h-12 w-12 text-accent/20" />
            <p className="text-accent/60">No alerts to display</p>
          </motion.div>
        )}
      </div>
      <div className="mt-6 border-t border-accent/20 pt-6 dark:border-accent/40">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="text-sm text-gray-900/60">{unreadCount} unread alerts</div>
          <div className="flex gap-2">
            <button className="rounded-md px-3 py-1.5 text-sm font-medium text-accent transition-colors hover:bg-accent/10 hover:text-text-primary dark:hover:bg-accent/20 dark:hover:text-bg-main">
              Mark All Read
            </button>
            <button className="rounded-md px-3 py-1.5 text-sm font-medium text-accent transition-colors hover:bg-accent/20 hover:text-text-primary dark:hover:bg-accent/40 dark:hover:text-bg-main">
              View All
            </button>
          </div>
        </div>
      </div>
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setDarkMode(dm => !dm)}
          className="rounded-full bg-primary p-3 text-bg-card shadow-lg transition-colors hover:bg-primary/90 dark:bg-primary dark:hover:bg-primary/80"
          aria-label="Toggle dark mode"
        >
          {darkMode ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 3v1m0 16v1m8.66-13.66l-.71.71M4.05 19.95l-.71.71M21 12h-1M4 12H3m16.66 5.66l-.71-.71M4.05 4.05l-.71-.71M16 12a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 12.79A9 9 0 1111.21 3a7 7 0 109.79 9.79z"
              />
            </svg>
          )}
        </button>
      </div>
    </motion.div>
  );
};

export default SystemAlertsPanel;
